import type { FC } from 'react';

import styled, { css, keyframes } from 'styled-components';
import type {
  BorderProps,
  ColorProps,
  HeightProps,
  MaxHeightProps,
  MaxWidthProps,
  MinHeightProps,
  MinWidthProps,
  OpacityProps,
  PositionProps,
  SpaceProps,
  TextAlignProps,
  VerticalAlignProps,
  WidthProps,
} from 'styled-system';
import {
  border as styledBorder,
  color as styledColor,
  height,
  maxHeight,
  maxWidth,
  minHeight,
  minWidth,
  opacity,
  position,
  space as styledSpace,
  textAlign,
  verticalAlign,
  width,
} from 'styled-system';



export const TableContainer = styled.div<
  { $radius?: boolean; $border?: boolean; $mobileCollapse?: boolean } & SpaceProps &
    MinHeightProps &
    HeightProps &
    MaxHeightProps &
    ColorProps
>(
  ({ theme: { colors, space, device }, $radius, $border, $mobileCollapse }) => css`
    position: relative;
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    min-height: 400px;
    background: ${colors.white};
    overflow: auto;
    overscroll-behavior-x: contain;
    -webkit-overflow-scrolling: touch;

    ${$radius &&
    css`
      border-radius: ${space[1]}px;
    `}

    ${$border &&
    css`
      border: 1px solid ${colors.background};
      border-bottom: none;
    `}

    ${styledSpace};
    ${minHeight};
    ${height};
    ${maxHeight};
    ${styledColor};

    ${$mobileCollapse &&
    css`
      ${device.mobile} {
        min-height: 280px;
      }
    `}
  `,
);

export const Table = styled.table`
  width: 100%;
  table-layout: auto;
  border-collapse: separate;
  border-spacing: 0;
`;

export const TableHead = styled.thead(
  ({ theme: { colors } }) => css`
    position: sticky;
    top: 0;
    z-index: 2;
    background: ${colors.background};

    & th:not(:last-child) {
      border-right: 1px solid ${colors.highlight.gray25};
    }

    & > tr:not(:first-child) > th {
      border-top: 1px solid ${colors.highlight.gray25};
      border-right: 1px solid ${colors.highlight.gray25};
    }
  `,
);

export const HeaderCell = styled.th<
  {
    $sticky?: boolean;
    $fixedWidth?: number | string;
    $striped?: boolean;
    $visibleByHover?: boolean;
  } & WidthProps &
    ColorProps &
    MinWidthProps &
    MaxWidthProps &
    HeightProps &
    SpaceProps &
    TextAlignProps &
    BorderProps &
    PositionProps
>(
  ({
    theme: { colors, space, fontSizes, device },
    $sticky,
    $fixedWidth,
    $striped,
    $visibleByHover,
  }) => css`
    height: 34px;
    min-width: 70px;
    white-space: nowrap;
    padding: ${space[1]}px;
    font-size: ${fontSizes[2]}px;
    font-weight: 600;
    letter-spacing: 0.5px;
    color: ${colors.text.disabled};
    background: ${$striped ? colors.white : colors.background};
    text-align: left;

    ${($fixedWidth || $fixedWidth === 0) &&
    css`
      width: ${typeof $fixedWidth === 'number' ? `${$fixedWidth}px` : $fixedWidth};
      min-width: ${typeof $fixedWidth === 'number'
        ? `${$fixedWidth}px`
        : $fixedWidth};
      max-width: ${typeof $fixedWidth === 'number'
        ? `${$fixedWidth}px`
        : $fixedWidth};
    `}

    ${$sticky &&
    css`
      background: ${$visibleByHover ? 'transparent' : colors.background};
      position: sticky;
    `}

    ${styledColor};
    ${width};
    ${minWidth};
    ${maxWidth};
    ${textAlign};
    ${height};
    ${styledSpace};
    ${styledBorder};
    ${position};

    ${device.mobile} {
      min-width: 120px;
      white-space: normal;
      line-height: 1.3;
      vertical-align: top;
    }
  `,
);

const progressAnimation = keyframes`
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(500%);
  }
`;

const TableLoadingOverlay = styled.div`
  width: 100%;
  height: calc(100% - 34px);
  position: absolute;
  top: 34px;
  z-index: 1;
  background: rgba(255, 255, 255, 0.4);
  overflow: hidden;
`;

const LoadingBar = styled.div(
  ({ theme: { colors } }) => css`
    position: absolute;
    top: 0;
    left: 0;
    width: 20%;
    height: 2px;
    background: ${colors.primary.main};
    animation: ${progressAnimation} 1.5s infinite linear;
  `,
);

export const TableLoading: FC = () => (
  <TableLoadingOverlay>
    <LoadingBar />
  </TableLoadingOverlay>
);

export const TableRow = styled.tr.attrs(() => ({
  role: 'row',
}))<
  {
    $horizontalLine?: boolean;
    $verticalLine?: boolean;
    $backlightLine?: boolean;
    $striped?: boolean;
  } & BorderProps &
    OpacityProps
>(
  ({
    theme: { colors },
    $horizontalLine,
    $verticalLine,
    $backlightLine,
    $striped,
  }) => css`
    ${$backlightLine &&
    css`
      &:hover td {
        background: ${colors.highlight.gray25};
      }
    `}

    ${$horizontalLine &&
    css`
      & > td,
      th {
        border-bottom: 1px solid ${colors.background};
      }
    `}

    ${$verticalLine &&
    css`
      & > td:not(:last-child) {
        border-right: 1px solid ${colors.background};
      }

      &
        > td[data-sticky='true']:not(:has(+ [data-sticky='true'])):not(
          :last-child
        )::after {
        content: '';
        position: absolute;
        top: 0;
        right: -2px;
        width: 2px;
        height: 100%;
        background: ${colors.highlight.gray40};
        pointer-events: none;
      }
    `}

    ${$striped &&
    css`
      &:nth-child(even) {
        background: ${colors.highlight.gray25};
      }
    `}

    ${styledColor}
    ${opacity}
    ${styledBorder}
  `,
);

export const DataCell = styled.td.attrs(() => ({
  role: 'cell',
}))<
  {
    $sticky?: boolean;
    $defaultHeight?: HeightProps['height'];
    $backlightLine?: boolean;
    $fixedWidth?: number | string;
    $truncate?: boolean;
    $visibleByHover?: boolean;
    $highlightColumn?: boolean;
  } & SpaceProps &
    BorderProps &
    TextAlignProps &
    VerticalAlignProps &
    ColorProps &
    PositionProps &
    HeightProps
>(
  ({
    theme: { fontSizes, space, colors, device },
    $sticky,
    $fixedWidth,
    $defaultHeight = 70,
    $backlightLine,
    $truncate,
    $visibleByHover,
    $highlightColumn,
  }) => css`
    max-width: none;
    padding: ${space[1]}px;
    height: ${typeof $defaultHeight === 'number'
      ? `${$defaultHeight}px`
      : $defaultHeight};
    font-weight: 500;
    font-size: ${fontSizes[3]}px;
    text-align: left;
    vertical-align: inherit;
    transition: background 0.1s ease-in-out;

    ${($fixedWidth || $fixedWidth === 0) &&
    css`
      width: ${typeof $fixedWidth === 'number' ? `${$fixedWidth}px` : $fixedWidth};
      min-width: ${typeof $fixedWidth === 'number'
        ? `${$fixedWidth}px`
        : $fixedWidth};
      max-width: ${typeof $fixedWidth === 'number'
        ? `${$fixedWidth}px`
        : $fixedWidth};
    `}

    ${!$truncate &&
    css`
      word-break: break-word;
      white-space: pre-wrap;
    `}

    ${$sticky &&
    css`
      position: sticky;
      z-index: 1;
      background: ${$visibleByHover ? 'inherit' : colors.white};

      ${$backlightLine &&
      css`
        tr:hover & {
          background: ${colors.highlight.gray25};
        }
      `}
    `}

    ${$visibleByHover &&
    css`
      & > * {
        opacity: 0;
      }

      tr:hover & > * {
        opacity: 1;
      }

      &:focus-within > * {
        opacity: 1;
      }
    `}

    ${$highlightColumn &&
    css`
      background-color: ${colors.highlight.gray25};
    `}

    ${styledColor}
    ${styledSpace}
    ${styledBorder}
    ${textAlign}
    ${verticalAlign};
    ${position};
    ${height};

    ${device.mobile} {
      min-width: 120px;
      height: auto;
      min-height: 52px;
      vertical-align: top;

      /* На touch-нет hover у строки: кнопки «действий» оставляем видимыми. */
      ${$visibleByHover &&
      css`
        & > * {
          opacity: 1;
        }
      `}
    }
  `,
);

export const TruncateSpan = styled.span`
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  ${({ theme }) => theme.device.mobile} {
    white-space: normal;
    overflow: visible;
    text-overflow: clip;
    word-break: break-word;
  }
`;

